import { Clipboard } from '@angular/cdk/clipboard';
import { Directive, EventEmitter, HostListener, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { TsvUtils } from '@ird/shared-utils';
import { TableComponent } from '../../components/table/table.component';

@Directive({
  selector: 'spx-table[spxClipboard]',
})
export class ClipboardDirective<T> implements OnInit, OnDestroy {
  @Input() clipboardColumnKeys: string[] = [];
  @Input() clipboardDefaultValues: Partial<T> = {} as Partial<T>;
  @Input() clipboardEnableCopy = true;
  @Input() clipboardEnablePaste = true;

  @Output() clipboardPasted = new EventEmitter<T[]>();
  @Output() clipboardCopied = new EventEmitter<T[]>();
  @Output() saveAllRequested = new EventEmitter<T[]>();
  @Output() discardAllRequested = new EventEmitter<void>();
  @Output() saveRowRequested = new EventEmitter<T>();
  @Output() discardRowRequested = new EventEmitter<T>();

  previewRows: T[] = [];
  previewRowSet: Set<T> = new Set();

  get hasPreviewRows(): boolean {
    return this.previewRows.length > 0;
  }

  constructor(
    private table: TableComponent<T>,
    private clipboard: Clipboard,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.table.clipboardDirective = this;
  }

  ngOnDestroy(): void {
    if (this.table.clipboardDirective === this) {
      this.table.clipboardDirective = undefined;
    }
  }

  @HostListener('document:copy', ['$event'])
  onCopy(event: Event): void {
    if (!this.clipboardEnableCopy) {
      return;
    }

    const clipboardEvent = event as ClipboardEvent;
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    const selected = this.table.selectionDirective?.selection?.selected;
    if (!selected || selected.length === 0) {
      return;
    }

    event.preventDefault();
    const keys = this.getColumnKeys();
    const tsv = TsvUtils.toTsv(selected, keys as (keyof T)[]);
    clipboardEvent.clipboardData?.setData('text/plain', tsv);
    this.clipboardCopied.emit(selected);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.hasPreviewRows) {
      this.onDiscardAllClicked();
    }
  }

  @HostListener('document:paste', ['$event'])
  onPaste(event: Event): void {
    if (!this.clipboardEnablePaste) {
      return;
    }

    const clipboardEvent = event as ClipboardEvent;
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    event.preventDefault();
    const text = clipboardEvent.clipboardData?.getData('text/plain');
    if (text && TsvUtils.isTsv(text)) {
      this.handlePastedText(text);
    }
  }

  copyRows(rows: T[]): void {
    if (!this.clipboardEnableCopy || rows.length === 0) {
      return;
    }
    const keys = this.getColumnKeys();
    const tsv = TsvUtils.toTsv(rows, keys as (keyof T)[]);
    this.clipboard.copy(tsv);
    this.clipboardCopied.emit(rows);
  }

  async pasteFromClipboard(): Promise<T[]> {
    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch (_err) {
      throw new Error('Clipboard access denied. Please allow clipboard permissions in your browser settings.');
    }
    return this.ngZone.run(() => this.handlePastedText(text));
  }

  addPreviewRows(rows: T[]): void {
    this.previewRows = [...rows];
    this.previewRowSet = new Set(this.previewRows);
    this.syncTablePreviewState();
  }

  removePreviewRow(row: T): void {
    this.previewRows = this.previewRows.filter((r) => r !== row);
    this.previewRowSet = new Set(this.previewRows);
    this.syncTablePreviewState();
  }

  clearPreviewRows(): void {
    this.previewRows = [];
    this.previewRowSet = new Set();
    this.syncTablePreviewState();
  }

  onSaveAllClicked(): void {
    this.saveAllRequested.emit([...this.previewRows]);
  }

  onDiscardAllClicked(): void {
    this.discardAllRequested.emit();
  }

  onSaveRowClicked(row: T): void {
    this.saveRowRequested.emit(row);
  }

  onDiscardRowClicked(row: T): void {
    this.discardRowRequested.emit(row);
  }

  private handlePastedText(text: string): T[] {
    if (!TsvUtils.isTsv(text)) {
      return [];
    }

    const keys = this.getColumnKeys();
    const { rows } = TsvUtils.parseTsv<T>(text, { columnKeys: keys, hasHeaders: true });
    if (rows.length === 0) {
      return [];
    }

    const merged = rows.map((row) => ({
      ...this.clipboardDefaultValues,
      ...row,
      id: null,
    })) as T[];

    this.addPreviewRows(merged);
    this.clipboardPasted.emit(merged);
    return merged;
  }

  private syncTablePreviewState(): void {
    this.table.previewRows = this.previewRowSet;
    this.table.clipboardPreviewColumns = this.hasPreviewRows ? ['spxClipboardPreviewBar'] : [];
  }

  private getColumnKeys(): string[] {
    if (this.clipboardColumnKeys.length > 0) {
      return this.clipboardColumnKeys;
    }
    return this.table.displayedColumns.filter((c) => c !== 'actions' && c !== 'spxSelectionCheckbox');
  }
}
